<template>
  <div class="tendencia-container">
    <div class="tendencia-header">
        <h1>Tendencia</h1>
      <p>Explora las últimas noticias en Tendencia</p>
    </div>

    <!-- Skeleton Loader Inicial -->
    <div v-if="loading && posts.length === 0" class="skeleton-list">
      <!-- Skeleton primer post -->
      <div class="skeleton-featured">
        <div class="skeleton-featured-meta"></div>
        <div class="skeleton-featured-title"></div>
        <div class="skeleton-featured-extract"></div>
        <div class="skeleton-featured-image"></div>
      </div>
      <!-- Skeleton resto de posts -->
      <div v-for="i in 5" :key="i" class="skeleton-item">
        <div class="skeleton-item-image"></div>
        <div class="skeleton-item-info">
          <div class="skeleton-item-meta"></div>
          <div class="skeleton-item-title"></div>
          <div class="skeleton-item-extract"></div>
        </div>
      </div>
    </div>

    <!-- Lista de posts -->
    <div v-else>
      <!-- PRIMER POST: diseño especial -->
      <div 
        v-if="posts.length > 0"
        class="featured-post"
        @click="goToPost(posts[0])"
      >
        <div class="featured-meta">
          <span class="featured-category">{{ posts[0].category?.name || 'Noticia' }}</span>
          <span class="featured-date">{{ formatDate(posts[0].created_at) }}</span>
        </div>
        <h2 class="featured-title">{{ posts[0].title }}</h2>
        <p class="featured-extract">{{ posts[0].extract }}</p>
        <img :src="getImageUrl(posts[0].image)" :alt="posts[0].title" class="featured-image">
      </div>

      <!-- RESTO DE POSTS: diseño horizontal -->
      <div class="posts-list">
        <div 
          v-for="post in posts.slice(1)" 
          :key="post.id"
          class="post-item"
          @click="goToPost(post)"
        >
          <img :src="getImageUrl(post.image)" :alt="post.title" class="post-image">
          <div class="post-info">
            <div class="post-meta">
              <span class="post-category">{{ post.category?.name || 'Noticia' }}</span>
              <span class="post-date">{{ formatDate(post.created_at) }}</span>
            </div>
            <h3 class="post-title">{{ post.title }}</h3>
            <p class="post-extract">{{ post.extract }}</p>
          </div>
        </div>
      </div>

      <!-- Botón Cargar Más -->
      <div v-if="hasMorePages" class="load-more-container">
        <button 
          class="load-more-btn" 
          @click="loadMore"
          :disabled="loadingMore"
        >
          <span v-if="loadingMore" class="loader"></span>
          <span v-else>Cargar más</span>
        </button>
      </div>
    </div>

    <!-- Skeleton Loader para Cargar Más -->
    <div v-if="loadingMore" class="skeleton-more">
      <div v-for="i in 3" :key="i" class="skeleton-item">
        <div class="skeleton-item-image"></div>
        <div class="skeleton-item-info">
          <div class="skeleton-item-meta"></div>
          <div class="skeleton-item-title"></div>
          <div class="skeleton-item-extract-one-line"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed  } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '../../services/api'

const router = useRouter()
const route = useRoute()
const posts = ref([])
const loading = ref(true)
const loadingMore = ref(false)
const currentPage = ref(1)
const lastPage = ref(1)

const getImageUrl = (image) => {
  if (!image) return '/noimagen.png'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000 / 60 / 60)
  
  if (diff < 1) return 'Hace unos minutos'
  if (diff < 24) return `Hace ${diff} horas`
  if (diff < 48) return 'Ayer'
  return d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const hasMorePages = computed(() => {
  return currentPage.value < lastPage.value
})

const loadPosts = async (reset = true) => {
  if (reset) {
    loading.value = true
    currentPage.value = 1
  }
  
  try {
    const response = await api.get(`/posts?page=${currentPage.value}`)
    const newPosts = response.data.posts.data || []
    
    if (reset) {
      posts.value = newPosts
    } else {
      posts.value = [...posts.value, ...newPosts]
    }
    
    lastPage.value = response.data.posts.last_page || 1
  } catch (error) {
    console.error('Error cargando posts en tendencia:', error)
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

const loadMore = async () => {
  if (loadingMore.value || !hasMorePages.value) return
  
  loadingMore.value = true
  currentPage.value++
  await loadPosts(false)
}

const goToPost = (post) => {
  router.push(`/noticia/${post.slug}`)
}

onMounted(() => {
  loadPosts()
})

watch(() => route.fullPath, () => {
  loadPosts()
})
</script>

<style scoped>
.tendencia-container {
  min-height: 100vh;
  background: #fff;
  padding: 20px;
  padding-top:80px;
 
}

.tendencia-header {
  text-align: center;
  margin-bottom: 30px;
}

.tendencia-header h1 {
  font-size: 28px;
  font-weight: bold;
  color: #a50000;
  margin-bottom: 8px;
}

.tendencia-header p {
  font-size: 14px;
  color: #666;
}

/* ========== PRIMER POST ========== */
.featured-post {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 30px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.featured-post:active {
  transform: scale(0.99);
}

.featured-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 4px;
}

.featured-date {
  font-size: 12px;
  color: #999;
}

.featured-category {
  font-size: 12px;
  color: #a50000;
  text-transform: uppercase;
  font-weight: bold;
  background: transparent;
  padding: 0;
}

.featured-title {
  font-size: 24px;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 12px;
  line-height: 1.3;
}

.featured-extract {
  font-size: 15px;
  color: #555;
  line-height: 1.5;
  margin-bottom: 20px;
}

.featured-image {
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 12px;
}

/* ========== RESTO DE POSTS ========== */
.posts-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.post-item {
  display: flex;
  gap: 15px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
  padding: 0;
}

.post-item:active {
  transform: scale(0.99);
}

.post-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  object-position: center;
  flex-shrink: 0;
  border-radius: 12px;
}

.post-info {
  flex: 1;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.post-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.post-category {
  font-size: 11px;
  color: #a50000;
  text-transform: uppercase;
  font-weight: bold;
  background: transparent;
  padding: 0;
}

.post-date {
  font-size: 11px;
  color: #999;
}

.post-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-extract {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ========== BOTÓN CARGAR MÁS ========== */
.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.load-more-btn {
  background: #a50000;
  color: white;
  border: none;
  padding: 12px 32px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.load-more-btn:hover:not(:disabled) {
  background: #cc0000;
  transform: scale(1.02);
}

.load-more-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loader {
  width: 20px;
  height: 20px;
  border: 2px solid white;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ========== SKELETON ========== */
.skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.skeleton-featured {
  background: white;
  border-radius: 16px;
  padding: 10px;
}

.skeleton-featured-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
}

.skeleton-featured-meta::before {
  content: '';
  width: 80px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-featured-meta::after {
  content: '';
  width: 100px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-featured-title {
  width: 90%;
  height: 28px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 15px;
}

.skeleton-featured-extract {
  width: 95%;
  height: 40px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 20px;
}

.skeleton-featured-image {
  width: 100%;
  height: 250px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-item {
  display: flex;
  gap: 15px;
}

.skeleton-item-image {
  width: 120px;
  height: 120px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
  flex-shrink: 0;
}

.skeleton-item-info {
  flex: 1;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.skeleton-item-meta {
  display: flex;
  justify-content: space-between;
}

.skeleton-item-meta::before {
  content: '';
  width: 80px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-item-meta::after {
  content: '';
  width: 100px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-item-title {
  width: 90%;
  height: 18px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-item-extract {
  width: 80%;
  height: 16px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-item-extract-one-line {
  width: 80%;
  height: 16px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0e0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-more {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .tendencia-container {
    padding: 15px;
    padding-top: 70px;
  }
  
  .tendencia-header h1 {
    font-size: 24px;
  }
  
  .featured-title {
    font-size: 20px;
  }
  
  .featured-extract {
    font-size: 14px;
  }
  
  .featured-image {
    max-height: 300px;
  }
  
  .post-image,
  .skeleton-item-image {
    width: 100px;
    height: 100px;
  }
  
  .post-title {
    font-size: 14px;
  }
  
  .post-extract {
    font-size: 12px;
  }
  
  .load-more-btn {
    padding: 10px 28px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .tendencia-container {
    padding-top: 100px;
  }
  
  .featured-title {
    font-size: 18px;
  }
  
  .featured-extract {
    font-size: 13px;
  }
  
  .featured-image {
    max-height: 220px;
  }
  
  .post-image,
  .skeleton-item-image {
    width: 90px;
    height: 90px;
  }
  
  .tendencia-header h1 {
    font-size: 22px;
  }
  
  .post-category,
  .post-date {
    font-size: 10px;
  }
  
  .load-more-btn {
    padding: 8px 24px;
    font-size: 13px;
    min-width: 140px;
  }
}
</style>